import React, { useState, useEffect } from 'react';
import { Button } from "../components/Button";
import { TextField } from "../components/TextField";
import { Select } from "../components/Select";
import { getEmailTemplates, getEmailTemplate, sendEmailWithTemplate } from '../services/api';

export function AdminEmails() {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templateContent, setTemplateContent] = useState(null);
  const [placeholders, setPlaceholders] = useState({});
  const [toEmail, setToEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showPreview, setShowPreview] = useState(true);
  const [showHTMLSource, setShowHTMLSource] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await getEmailTemplates();
      console.log('Templates response:', response);
      if (response && response.templates) {
        setTemplates(response.templates);
        setMessage('');
      } else if (response && Array.isArray(response)) {
        setTemplates(response);
        setMessage('');
      } else {
        setMessage('No templates found');
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setMessage('Error fetching email templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplateContent = async (templateName) => {
    setLoading(true);
    try {
      const response = await getEmailTemplate(templateName);
      if (response && response.template) {
        const templateData =  response;

        // Ensure placeholders is always an array
        if (templateData.placeholders && !Array.isArray(templateData.placeholders)) {
          templateData.placeholders = [];
        } else if (!templateData.placeholders) {
          // Set default placeholders based on template name
          throw new Error("No placeholders received");

        }

        setTemplateContent(templateData);
        const initialPlaceholders = {};
        templateData.placeholders.forEach(placeholder => {
          initialPlaceholders[placeholder] = '';
        });
        setPlaceholders(initialPlaceholders);
        setMessage('');
      } else {
        setMessage('Template not found or has no content');
        setTemplateContent(null);
        setPlaceholders({});
      }
    } catch (error) {
      console.error('Error fetching template content:', error);
      setMessage('Error fetching template content');
      setTemplateContent(null);
      setPlaceholders({});
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (templateName) => {
    setSelectedTemplate(templateName);
    if (templateName) {
      fetchTemplateContent(templateName);
    } else {
      setTemplateContent(null);
      setPlaceholders({});
    }
  };

  const handlePlaceholderChange = (placeholder, value) => {
    setPlaceholders(prev => ({
      ...prev,
      [placeholder]: value
    }));
  };



const generateEmailHTML = () => {
    if (!templateContent || !templateContent.content) return '';

    // Use only user-provided placeholder values
    let html = templateContent.content;
    Object.keys(placeholders).forEach(key => {
      // Skip replacement if the placeholder is "endif"
      if (key !== 'endif') {
        html = html.replace(new RegExp(`\{\{${key}\}\}`, 'g'), placeholders[key] || '');
      }
    });

    return html;
  };

// const areAllPlaceholdersFilled = () => {
//     if (!templateContent || !templateContent.placeholders) return true;
//     return templateContent.placeholders.every(placeholder =>
//       placeholders[placeholder] && placeholders[placeholder].trim() !== ''
//     );
//   };

  const sendEmail = async () => {
    if (!selectedTemplate) {
      setMessage('Please select a template');
      return;
    }

    if (!toEmail) {
      setMessage('Please enter recipient email');
      return;
    }

    // if (!areAllPlaceholdersFilled()) {
    //   setMessage('Please fill in all template parameters');
    //   return;
    // }

    // if (!validateEmail(toEmail)) {
    //   setMessage('Please enter a valid email address');
    //   return;
    // }

    setLoading(true);
    setMessage('');

    try {
      const response = await sendEmailWithTemplate({
        template: selectedTemplate,
        to_email: toEmail,
        placeholders: placeholders
      });

      console.log('Send email response:', response);

      if (response && (response.msg || response.data?.msg)) {
        setMessage('Email sent successfully!');
        setToEmail('');
        const initialPlaceholders = {};
        if (templateContent && templateContent.placeholders && Array.isArray(templateContent.placeholders)) {
          templateContent.placeholders.forEach(placeholder => {
            initialPlaceholders[placeholder] = '';
          });
        }
        setPlaceholders(initialPlaceholders);
      } else {
        setMessage(response?.msg || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setMessage(error.response?.data?.msg || 'Error sending email');
    } finally {
      setLoading(false);
    }
  };

return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1>Admin Email Manager</h1>

      {message && (
        <div style={{
          padding: '10px',
          marginBottom: '20px',
          backgroundColor: message.includes('success') ? '#d4edda' : '#f8d7da',
          color: message.includes('success') ? '#155724' : '#721c24',
          borderRadius: '4px',
          border: `1px solid ${message.includes('success') ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {message}
        </div>
      )}

      <div style={{ display: 'flex', gap: '20px' }}>
        {/* Left Column - Form Fields */}
        <div style={{ flex: 1, minWidth: '0' }}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Email Template:
            </label>
            <Select
              value={selectedTemplate}
              onChange={(e) => handleTemplateChange(e.target.value)}
              options={[
                'Select a template...',
                ...(templates || [])
              ]}
              disabled={loading}
            />
            {loading && !selectedTemplate && (
              <div style={{ marginTop: '5px', fontSize: '12px', color: '#6c757d' }}>
                Loading templates...
              </div>
            )}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <TextField
              label="Recipient Email"
              type="email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              placeholder="Enter recipient email address"
              fullWidth
            />
          </div>

          {/* Placeholder Input Fields */}
          {templateContent && templateContent.placeholders && templateContent.placeholders.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h3>Template Placeholders</h3>
              {templateContent.placeholders.map(placeholder => (
                <div key={placeholder} style={{ marginBottom: '10px' }}>
                  <TextField
                    label={placeholder.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    value={placeholders[placeholder] || ''}
                    onChange={(e) => handlePlaceholderChange(placeholder, e.target.value)}
                    placeholder={`Enter ${placeholder}`}
                    fullWidth
                  />
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <Button
              onClick={() => {
                setSelectedTemplate('');
                setTemplateContent(null);
                setPlaceholders({});
                setToEmail('');
                setMessage('');
              }}
              color="black"
              label="Reset"
            />

            <Button
              color="green"
              label={loading ? 'Sending...' : 'Send Email'}
              onClick={sendEmail}
              disabled={loading || !selectedTemplate || !toEmail }
            />
          </div>
        </div>

        {/* Right Column - Email Preview */}
        {templateContent && (
          <div style={{ flex: 1, minWidth: '0' }}>
            <h3>Rendered Email Preview</h3>

            {/* HTML Email Preview */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                backgroundColor: '#ffffff',
                maxHeight: '600px',
                overflow: 'auto'
              }}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: generateEmailHTML()
                  }}
                  style={{
                    padding: '20px',
                    fontFamily: 'Arial, sans-serif'
                  }}
                />
              </div>
            </div>

            {/* View Source Button */}
            <div style={{ marginBottom: '20px' }}>
              <Button
                onClick={() => setShowHTMLSource(!showHTMLSource)}
                label={showHTMLSource ? 'Hide HTML Source' : 'View HTML Source'}
                color={'red'}
              />
            </div>

            {/* HTML Source Code */}
            {showHTMLSource && (
              <div style={{ marginTop: '20px' }}>
                <h4>HTML Source Code</h4>
                <div style={{
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  padding: '15px',
                  fontFamily: 'Courier New, monospace',
                  fontSize: '12px',
                  maxHeight: '300px',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap'
                }}>
                  {generateEmailHTML()}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}